import { DEFAULT_CURRENCY } from "@repo/shared";
import type { AssetClass, PortfolioCollectionCalculationsMode } from "@repo/shared/types";
import { ListOrdered, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useDeleteAssetClass } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { AssetClassDialog } from "./asset-class-dialog";
import { T } from "./asset-classes-table.i18n";
import { SelectionsDialog } from "./selections-dialog";

type AssetClassesTableProps = {
  portfolioCollectionId: string;
  assetClasses: AssetClass[];
  currency?: string;
  riskFreeRatePct?: number;
  calculationsMode?: PortfolioCollectionCalculationsMode;
};

export function AssetClassesTable({
  portfolioCollectionId,
  assetClasses,
  currency = DEFAULT_CURRENCY,
  riskFreeRatePct = 2.5,
  calculationsMode = "asset_classes",
}: AssetClassesTableProps) {
  const localize = useLocalize();
  const t = useT(T);

  const deleteAssetClass = useDeleteAssetClass();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<AssetClass | null>(null);

  const [selectionsClass, setSelectionsClass] = useState<AssetClass | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — an asset class the server would refuse to delete shows why on
  // the Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (acId) => pcApi.checkDeleteAssetClass(portfolioCollectionId, acId),
    onAllowed: (acId) => {
      setClassToDelete(acId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleCreate = () => {
    setEditingClass(null);
    setDialogOpen(true);
  };

  const handleEdit = (assetClass: AssetClass) => {
    setEditingClass(assetClass);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingClass(null);
  };

  const handleDelete = async () => {
    if (!classToDelete) return;

    deleteAssetClass.mutate(
      { collectionId: portfolioCollectionId, acId: classToDelete },
      {
        onSuccess: () => {
          setClassToDelete(null);
        },
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setClassToDelete(null);
        },
      },
    );
  };

  const isPending = deleteAssetClass.isPending;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={handleCreate} disabled={isPending}>
            {t.addAssetClass}
          </Button>
        </div>

        {assetClasses.length === 0 ? (
          <EmptyState>{t.noAssetClasses}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.linkedInstrument}</TableHead>
                <TableHead>{t.products}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assetClasses.map((assetClass) => (
                <TableRow key={assetClass.id}>
                  <TableCell className="font-medium">{localize(assetClass.nameI18n)}</TableCell>
                  <TableCell>
                    {assetClass.ticker && assetClass.dataset ? (
                      <code className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        {assetClass.dataset}:{assetClass.ticker}
                      </code>
                    ) : (
                      <span className="text-muted-foreground italic text-xs">{t.notLinked}</span>
                    )}
                  </TableCell>
                  <TableCell>{assetClass._count?.productAllocations ?? 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        tooltip={t.selections}
                        onClick={() => setSelectionsClass(assetClass)}
                        disabled={isPending}
                      >
                        <ListOrdered className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => handleEdit(assetClass)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => requestDelete(assetClass.id)}
                        loading={checkingId === assetClass.id}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AssetClassDialog
        open={dialogOpen}
        portfolioCollectionId={portfolioCollectionId}
        assetClass={editingClass}
        onClose={handleDialogClose}
        currency={currency}
        riskFreeRatePct={riskFreeRatePct}
        calculationsMode={calculationsMode}
      />

      <SelectionsDialog
        open={!!selectionsClass}
        portfolioCollectionId={portfolioCollectionId}
        assetClass={selectionsClass}
        onClose={() => setSelectionsClass(null)}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteTitle}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
